import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

export default function SEO({
    title,
    description,
    canonical,
    keywords,
    ogType = 'website',
    ogImage = 'https://www.blinqbooks.com/logo.png', // Use absolute URL for og:image
    twitterCard = 'summary_large_image',
    schema, // New custom schema prop
    robots = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
}) {
    const location = useLocation();
    const siteName = 'blinqbooks';
    const siteUrl = 'https://www.blinqbooks.com';
    const fullTitle = title ? `${title} | ${siteName}` : siteName;
    const defaultDescription = 'The All-In-One Platform Made for Modern Accounting. Scale faster with our AI-driven ERP suite covering accounting, POS, and inventory.';

    // Auto-generate canonical URL from current route if not explicitly provided
    const cleanPath = location.pathname.endsWith('/') && location.pathname.length > 1
        ? location.pathname.slice(0, -1)
        : location.pathname;
    const canonicalUrl = canonical || `${siteUrl}${cleanPath === '' ? '/' : cleanPath}`;

    // Ensure og:image is absolute
    const absoluteOgImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;

    const defaultSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "blinqbooks",
        "alternateName": "blinqbook",
        "url": "https://www.blinqbooks.com",
        "logo": "https://www.blinqbooks.com/logo.png",
        "description": defaultDescription,
        "sameAs": [
            "https://www.facebook.com/blinqbooks/",
            "https://www.instagram.com/blinqbooks",
            "https://x.com/blinqbooks",
            "https://linkedin.com/company/blinqbooks"
        ]
    };

    // Combine or choose the schema markup
    const renderSchemas = () => {
        if (!schema) {
            return <script type="application/ld+json">{JSON.stringify(defaultSchema)}</script>;
        }
        
        const schemasArray = Array.isArray(schema) ? [defaultSchema, ...schema] : [defaultSchema, schema];
        return schemasArray.map((sch, index) => (
            <script key={index} type="application/ld+json">
                {JSON.stringify(sch)}
            </script>
        ));
    };

    return (
        <Helmet>
            {/* Favicon / Site Icon */}
            <link rel="icon" href="/favicon.ico" sizes="any" />
            <link rel="icon" type="image/png" href="/icon.png" />
            <link rel="apple-touch-icon" href="/logo.png" />

            {/* Basic Meta Tags */}
            <title>{fullTitle}</title>
            <meta name="description" content={description || defaultDescription} />
            <meta name="keywords" content={keywords || "blinqbooks, blinqbook, ERP, AI Business, Accounting Software, POS, Business Management"} />
            <link rel="canonical" href={canonicalUrl} />

            {/* Crawling & Indexing Directives */}
            <meta name="robots" content={robots} />
            <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
            <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={ogType} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description || defaultDescription} />
            <meta property="og:image" content={absoluteOgImage} />
            <meta property="og:image:type" content="image/png" />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:site_name" content={siteName} />
            <meta property="og:locale" content="en_US" />

            {/* Twitter */}
            <meta name="twitter:card" content={twitterCard} />
            <meta name="twitter:site" content="@blinqbooks" />
            <meta name="twitter:creator" content="@blinqbooks" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description || defaultDescription} />
            <meta name="twitter:image" content={absoluteOgImage} />

            {/* Structured Data */}
            {renderSchemas()}
        </Helmet>
    );
}

